import React from 'react';
import { Link } from 'react-router-dom';
import { CATEGORIES, MENU } from '../data/menu';
import { useCart } from '../state/cart/CartContext';
import { useCartDrawer } from '../state/ui/CartDrawerContext';
import { ContactDetails } from '../components/layout/ContactDetails';
import { HeroPromoText } from '../components/layout/HeroPromoText';
import { OrdersPausedNotice } from '../components/layout/OrdersPausedNotice';
import { HeroBackground } from '../components/layout/HeroBackground';
import { getActiveHeroPromo, getCategories, getDeals, getHeroDealImageUrl, getMenuBackgroundImageUrl, getMenuItems, getMunchBoxes } from '../services/dashboardService';
import { useOrderingAvailability } from '../hooks/useOrderingAvailability';
import { DealWithSlots } from '../types/app';
import { MenuItem } from '../state/cart/cartTypes';

const HOME_MENU_STATE_KEY = 'chunkzters:home-menu-state';

function readSavedHomeMenuState(storageKey: string = HOME_MENU_STATE_KEY): { activeCategory?: string; scrollY?: number } {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { activeCategory?: string; scrollY?: number };
    return {
      activeCategory: typeof parsed.activeCategory === 'string' ? parsed.activeCategory : undefined,
      scrollY: Number.isFinite(parsed.scrollY) ? parsed.scrollY : undefined,
    };
  } catch {
    return {};
  }
}

function saveHomeMenuState(storageKey: string, activeCategory: string, scrollY?: number) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        activeCategory,
        scrollY: Number.isFinite(scrollY) ? scrollY : undefined,
      })
    );
  } catch {
    // Ignore storage failures.
  }
}

const DEALS_CATEGORY = 'Deals';
const MUNCH_BOXES_CATEGORY = 'Munch Boxes';
const TEST_MENU_PREFIX = '/test-menu';

type HomePageProps = {
  testMode?: boolean;
};

function menuStateKey(testMode: boolean): string {
  return testMode ? 'chunkzters:test-menu-state' : 'chunkzters:home-menu-state';
}

export function HomePage({ testMode = false }: HomePageProps) {
  const initialSavedState = React.useMemo(() => readSavedHomeMenuState(menuStateKey(testMode)), [testMode]);
  const cart = useCart();
  const { openCart } = useCartDrawer();
  const [active, setActive] = React.useState<string>(initialSavedState.activeCategory || '');
  const [menuLoading, setMenuLoading] = React.useState(true);
  const [categories, setCategories] = React.useState<string[]>([]);
  const [menuItems, setMenuItems] = React.useState<MenuItem[]>([]);
  const [deals, setDeals] = React.useState<DealWithSlots[]>([]);
  const [munchBoxes, setMunchBoxes] = React.useState<DealWithSlots[]>([]);
  const [scrollRestored, setScrollRestored] = React.useState(false);
  const [loadedImageBySrc, setLoadedImageBySrc] = React.useState<Record<string, boolean>>({});
  const [heroPromoText, setHeroPromoText] = React.useState<string | null>(null);
  const [heroDealImageUrl, setHeroDealImageUrl] = React.useState<string | null>(null);
  const [menuBackgroundImageUrl, setMenuBackgroundImageUrl] = React.useState<string | null>(null);
  const {
    paused: ordersPaused,
    outsideHours,
    acceptingOrders,
    message: orderingMessage,
    loading: orderingLoading,
  } = useOrderingAvailability();
  const menuStateStorageKey = menuStateKey(testMode);
  const menuBasePath = testMode ? TEST_MENU_PREFIX : '';
  const showOrdersPaused = !testMode && ordersPaused;
  const showOutsideHours = !testMode && !ordersPaused && outsideHours;
  const orderingBlocked = !testMode && !acceptingOrders;

  React.useEffect(() => {
    void getActiveHeroPromo()
      .then(setHeroPromoText)
      .catch(() => setHeroPromoText(null));
    void getHeroDealImageUrl()
      .then(setHeroDealImageUrl)
      .catch(() => setHeroDealImageUrl(null));
    void getMenuBackgroundImageUrl()
      .then(setMenuBackgroundImageUrl)
      .catch(() => setMenuBackgroundImageUrl(null));
  }, []);

  React.useEffect(() => {
    if (!testMode && orderingLoading) return;
    if (!testMode && ordersPaused) {
      setMenuLoading(false);
      return;
    }
    async function loadFromSupabase() {
      try {
        const [cats, items, dealRows, munchBoxRows] = await Promise.all([
          getCategories(),
          getMenuItems(),
          getDeals(),
          getMunchBoxes(),
        ]);
        const nextCategories = cats.map((c) => c.name);
        if (dealRows.length) {
          if (!nextCategories.includes(DEALS_CATEGORY)) {
            nextCategories.unshift(DEALS_CATEGORY);
          }
        }
        if (munchBoxRows.length) {
          if (!nextCategories.includes(MUNCH_BOXES_CATEGORY)) {
            const dealsIndex = nextCategories.indexOf(DEALS_CATEGORY);
            if (dealsIndex >= 0) {
              nextCategories.splice(dealsIndex + 1, 0, MUNCH_BOXES_CATEGORY);
            } else {
              nextCategories.unshift(MUNCH_BOXES_CATEGORY);
            }
          }
        }
        setDeals(dealRows);
        setMunchBoxes(munchBoxRows);
        const categoryById = Object.fromEntries(cats.map((c) => [c.id, c.name]));
        const nextMenuItems: MenuItem[] = items.map((m) => ({
          id: m.id,
          name: m.name,
          description: m.description,
          price: Number(m.price),
          imageUrl: m.image_url ?? undefined,
          category: categoryById[m.category_id] ?? 'Other',
          isSoldOut: m.is_sold_out || !m.is_available,
          includesDrinkChoice: m.includes_drink_choice,
          eligibleForDiscount: m.eligible_for_discount !== false,
          offersMealUpgrade: m.offers_meal_upgrade === true,
          saladSaucePreferenceListId: m.salad_sauce_preference_list_id || undefined,
          flavourChoiceListId: m.flavour_choice_list_id || undefined,
        }));

        if (nextCategories.length) setCategories(nextCategories);
        if (nextMenuItems.length) setMenuItems(nextMenuItems);
      } catch {
        setCategories(CATEGORIES);
        setMenuItems(MENU);
      } finally {
        setMenuLoading(false);
      }
    }
    void loadFromSupabase();
  }, [orderingLoading, ordersPaused, testMode]);

  React.useEffect(() => {
    if (!active && categories.length) {
      setActive(categories[0]);
    }
  }, [active, categories]);

  React.useEffect(() => {
    if (!active) return;
    saveHomeMenuState(menuStateStorageKey, active);
  }, [active, menuStateStorageKey]);

  React.useEffect(() => {
    if (scrollRestored) return;
    if (typeof initialSavedState.scrollY !== 'number') {
      setScrollRestored(true);
      return;
    }
    const raf = window.requestAnimationFrame(() => {
      window.scrollTo({ top: initialSavedState.scrollY || 0, behavior: 'auto' });
      setScrollRestored(true);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [initialSavedState.scrollY, scrollRestored]);

  React.useEffect(
    () => () => {
      saveHomeMenuState(menuStateStorageKey, active, window.scrollY);
    },
    [active, menuStateStorageKey]
  );

  const items = React.useMemo(
    () =>
      menuItems.filter((m) =>
        active && active !== DEALS_CATEGORY && active !== MUNCH_BOXES_CATEGORY ? m.category === active : false
      ),
    [active, menuItems]
  );

  const showingDeals = active === DEALS_CATEGORY;
  const showingMunchBoxes = active === MUNCH_BOXES_CATEGORY;
  const activeBundles = showingDeals ? deals : showingMunchBoxes ? munchBoxes : [];
  const bundleLinkPrefix = showingMunchBoxes ? `${menuBasePath}/munch-box` : `${menuBasePath}/deal`;
  const bundleFallbackDescription = showingMunchBoxes
    ? 'Munch box — configure your choices'
    : 'Bundle deal — configure your choices';

  const markImageLoaded = React.useCallback((src: string) => {
    setLoadedImageBySrc((prev) => {
      if (prev[src]) return prev;
      return { ...prev, [src]: true };
    });
  }, []);

  return (
    <>
      <div className="chunkzters-hero chunkzters-heroFull">
        <div className="container-fluid px-0">
          <HeroBackground priority imageUrl={heroDealImageUrl}>
            <div className="p-4 p-md-5 text-center">
              <div className="d-flex flex-column flex-lg-row align-items-center justify-content-center gap-4 gap-lg-5 mb-3">
                <div className="display-4 fw-bold px-2 text-white" style={{ textShadow: '0 2px 16px rgba(0, 0, 0, 0.5)' }}>
                  Food So Good You Order Twice
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2 mb-3 justify-content-center mt-2">
                <span className="chunkzters-chip">
                  <i className="bi bi-lightning-charge" /> Fast checkout
                </span>
                <span className="chunkzters-chip">
                  <i className="bi bi-bag-check" /> Pickup
                </span>
              </div>
              <ContactDetails className="chunkzters-contact--hero" />
              {showOrdersPaused && orderingMessage ? (
                <OrdersPausedNotice message={orderingMessage} variant="hero" reason="paused" />
              ) : showOutsideHours && orderingMessage ? (
                <OrdersPausedNotice message={orderingMessage} variant="hero" reason="outside_hours" />
              ) : heroPromoText ? (
                <HeroPromoText text={heroPromoText} />
              ) : null}
            </div>
          </HeroBackground>
        </div>
      </div>

      <div
        className={`chunkzters-home-menu${menuBackgroundImageUrl ? ' chunkzters-home-menu--hasImage' : ''}`}
        style={
          menuBackgroundImageUrl
            ? ({
                ['--chunkzters-menu-bg-image' as string]: `url(${JSON.stringify(menuBackgroundImageUrl)})`,
              } as React.CSSProperties)
            : undefined
        }
      >
      <div className="container chunkzters-container pt-1 pt-md-2 pb-4 pb-md-5">
        {testMode ? (
          <div className="alert alert-warning d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2 mb-4">
            <span>
              <strong>Test menu.</strong> Browse the full menu here while orders are paused on the homepage.
            </span>
            <Link to="/" className="btn btn-sm btn-outline-dark flex-shrink-0">
              Live homepage
            </Link>
          </div>
        ) : null}
        <div className="row g-4">
        {!testMode && orderingLoading ? (
          <div className="col-12">
            <div className="chunkzters-card p-5 d-flex flex-column align-items-center justify-content-center text-center">
              <div className="spinner-border text-secondary" role="status" aria-label="Loading">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-3 text-muted">Loading...</div>
            </div>
          </div>
        ) : showOrdersPaused && orderingMessage ? (
          <div className="col-12">
            <OrdersPausedNotice message={orderingMessage} reason="paused" />
          </div>
        ) : menuLoading ? (
          <div className="col-12">
            <div className="chunkzters-card p-5 d-flex flex-column align-items-center justify-content-center text-center">
              <div className="spinner-border text-secondary" role="status" aria-label="Loading">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-3 text-muted">Loading menu...</div>
            </div>
          </div>
        ) : (
          <>
        {showOutsideHours && orderingMessage ? (
          <div className="col-12">
            <div className="alert alert-warning mb-0" role="status">
              <div className="fw-semibold mb-1">
                <i className="bi bi-clock me-1" aria-hidden />
                Currently closed
              </div>
              <div className="small mb-0">{orderingMessage}</div>
            </div>
          </div>
        ) : null}
        <div className="col-12 col-lg-3 d-none d-lg-block">
          <div className="chunkzters-card p-3">
            <div className="fw-bold mb-2">Categories</div>
            <div className="d-grid gap-2">
              {categories.map((c) => (
                <div
                  key={c}
                  className={`chunkzters-category ${c === active ? 'chunkzters-categoryActive' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setActive(c)}
                  onKeyDown={(e) => (e.key === 'Enter' ? setActive(c) : undefined)}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="fw-semibold">{c}</span>
                    <i className="bi bi-chevron-right" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-9">
          <div className="chunkzters-card p-3 d-lg-none mb-3">
            <label htmlFor="mobile-category-select" className="fw-bold mb-2 d-block">
              Categories
            </label>
            <select
              id="mobile-category-select"
              className="form-select chunkzters-input"
              value={active}
              onChange={(e) => setActive(e.target.value)}
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="d-flex align-items-center justify-content-between mb-5">
            <div>
              <div className="fw-bold" style={{ fontSize: 18 }}>
                {active || 'Menu'}
              </div>
              <div style={{ color: 'var(--chunkzters-ink-muted)', fontSize: 13 }}>
                {showingDeals || showingMunchBoxes
                  ? `${activeBundles.length} ${activeBundles.length === 1 ? 'option' : 'options'}`
                  : `${items.length} item${items.length === 1 ? '' : 's'}`}
              </div>
            </div>
            <button type="button" className="btn btn-sm chunkzters-btn-primary" onClick={openCart}>
              View basket ({cart.count})
            </button>
          </div>

          <div className="row g-3">
            {showingDeals || showingMunchBoxes
              ? activeBundles.map((deal) => {
                  const imageSrc = deal.image_url || `${process.env.PUBLIC_URL}/assets/homepage_2.png`;
                  const isImageLoaded = loadedImageBySrc[imageSrc] === true;
                  return (
                  <div className="col-12 col-md-6" key={deal.id}>
                    <div className="chunkzters-itemCard">
                      <div className="position-relative">
                        {!isImageLoaded ? (
                          <div className="position-absolute top-50 start-50 translate-middle">
                            <div className="spinner-border spinner-border-sm text-secondary" role="status">
                              <span className="visually-hidden">Loading image...</span>
                            </div>
                          </div>
                        ) : null}
                        <img
                          src={imageSrc}
                          alt={deal.name}
                          className="chunkzters-itemThumb"
                          loading="lazy"
                          decoding="async"
                          style={{ opacity: isImageLoaded ? 1 : 0, transition: 'opacity 0.2s ease' }}
                          onLoad={() => markImageLoaded(imageSrc)}
                          onError={() => markImageLoaded(imageSrc)}
                        />
                      </div>
                      <div className="p-3">
                        <div className="fw-bold">{deal.name}</div>
                        <div className="mt-1 small" style={{ color: 'var(--chunkzters-ink-muted)' }}>
                          {deal.description || bundleFallbackDescription}
                        </div>
                        <div className="mt-3 d-flex align-items-center justify-content-between gap-3">
                          <div className="fw-semibold">£ {Number(deal.price).toFixed(2)}</div>
                          <Link
                            to={`${bundleLinkPrefix}/${deal.id}`}
                            className="btn btn-sm chunkzters-btn-primary d-inline-flex align-items-center gap-2 flex-shrink-0"
                            onClick={() => saveHomeMenuState(menuStateStorageKey, active, window.scrollY)}
                          >
                            <i className="bi bi-sliders" aria-hidden />
                            Configure
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })
              : items.map((m) => (
              <div className="col-12 col-md-6" key={m.id}>
                <div className="chunkzters-itemCard">
                  {(() => {
                    const imageSrc = m.imageUrl || `${process.env.PUBLIC_URL}/assets/homepage_2.png`;
                    const isImageLoaded = loadedImageBySrc[imageSrc] === true;
                    return (
                      <div className="position-relative">
                        {!isImageLoaded ? (
                          <div className="position-absolute top-50 start-50 translate-middle">
                            <div className="spinner-border spinner-border-sm text-secondary" role="status">
                              <span className="visually-hidden">Loading image...</span>
                            </div>
                          </div>
                        ) : null}
                        <img
                          src={imageSrc}
                          alt={m.name}
                          className="chunkzters-itemThumb"
                          loading="lazy"
                          decoding="async"
                          style={{ opacity: isImageLoaded ? 1 : 0, transition: 'opacity 0.2s ease' }}
                          onLoad={() => markImageLoaded(imageSrc)}
                          onError={() => markImageLoaded(imageSrc)}
                        />
                      </div>
                    );
                  })()}
                  <div className="p-3">
                    <div className="fw-bold">{m.name}</div>
                    <div className="mt-1 small" style={{ color: 'var(--chunkzters-ink-muted)' }}>
                      {m.description}
                    </div>
                    <div className="mt-3 d-flex align-items-center gap-2">
                      <div className="fw-semibold flex-grow-1">£ {m.price.toFixed(2)}</div>
                      {m.isSoldOut ? <span className="badge text-bg-danger">Sold out</span> : null}
                      <div className="chunkzters-item-actions ms-auto">
                        <Link
                          to={`${menuBasePath}/item/${m.id}`}
                          className="btn btn-sm btn-outline-dark chunkzters-item-action-btn"
                          onClick={() => saveHomeMenuState(menuStateStorageKey, active, window.scrollY)}
                          aria-label={`View ${m.name}`}
                        >
                          <i className="bi bi-eye" aria-hidden />
                        </Link>
                        {m.includesDrinkChoice ||
                        m.offersMealUpgrade ||
                        Boolean(m.flavourChoiceListId) ||
                        Boolean(m.saladSaucePreferenceListId) ? (
                          <Link
                            to={`${menuBasePath}/item/${m.id}`}
                            className="btn btn-sm chunkzters-btn-primary chunkzters-item-action-btn d-inline-flex align-items-center gap-1"
                            onClick={() => saveHomeMenuState(menuStateStorageKey, active, window.scrollY)}
                          >
                            <i className="bi bi-sliders" aria-hidden />
                            Configure
                          </Link>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm chunkzters-btn-primary chunkzters-item-action-btn"
                            disabled={m.isSoldOut || orderingBlocked}
                            onClick={() => cart.addItem(m, 1)}
                          >
                            <i className="bi bi-plus-lg" /> {orderingBlocked ? 'Closed' : 'Add'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
          </>
        )}
      </div>
      </div>
      </div>
    </>
  );
}

